import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group';

const githubEnterpriseFormSchema = z.object({
  accountName: z.string().min(1, 'Enter a name for this integration'),
  username: z.string().min(1, 'Enter your GitHub username'),
  password: z.string().min(1, 'Enter your personal access token'),
  accountUrl: z
    .string()
    .min(1, 'Enter your GitHub Enterprise instance URL')
    .transform((val) => `https://${val}`),
});

export type IGitHubEnterpriseForm = z.infer<typeof githubEnterpriseFormSchema>;

type Props = {
  onSubmit: (data: IGitHubEnterpriseForm) => Promise<void>;
  onBack: () => void;
  accountName?: string;
  accountUrl?: string;
  btnText?: React.ReactNode;
};

const GitHubEnterpriseForm = ({ onSubmit, onBack, accountName = '', btnText, accountUrl = '' }: Props) => {
  const form = useForm<IGitHubEnterpriseForm>({
    resolver: zodResolver(githubEnterpriseFormSchema),
    defaultValues: {
      accountName,
      username: '',
      password: '',
      accountUrl,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="accountName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Integration name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g., My GitHub Enterprise Org name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="accountUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>GitHub Enterprise instance URL</FormLabel>
                <InputGroup>
                  <InputGroupAddon>
                    <InputGroupText>https://</InputGroupText>
                  </InputGroupAddon>
                  <FormControl>
                    <InputGroupInput {...field} placeholder="github.yourcompany.com" className="pl-2" />
                  </FormControl>
                </InputGroup>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="your-username" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Personal access token</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <Button size="sm" variant="outline" className="sm:flex-1" onClick={onBack}>
            <ArrowLeftIcon /> View instructions
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            className="sm:flex-1"
          >
            {btnText ?? (
              <Fragment>
                Submit <SendIcon />
              </Fragment>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default GitHubEnterpriseForm;
